import request from '@/utils/request'
import type { AxiosResponse } from 'axios'
import type { LaravelResource } from '../types'
import type { ContactNumberingPayload, ContactNumberingSettings } from '@/models/ContactNumbering'
import type { ContactDataType, ContactDataTypes } from '@/models/ContactDataType'

export interface PersonalizationData {
  logo?: string | null
}

/** GET /api/v2/settings/personalization */
export function getPersonalizationData(): Promise<AxiosResponse<PersonalizationData>> {
  return request({
    url: '/api/v2/settings/personalization',
    method: 'get',
  })
}

export interface TimeTrackingSettings {
  account_id: string
  /** Used to convert an estimation given in days; default 8. */
  hours_per_day: number
  /** Billing flag only; IANA zone that defines the booking day, default Europe/Vienna. */
  billing_timezone?: string
}

/** GET /api/v2/settings/time-tracking */
export function getTimeTrackingSettings(): Promise<
  AxiosResponse<LaravelResource<TimeTrackingSettings>>
> {
  return request({
    url: '/api/v2/settings/time-tracking',
    method: 'get',
  })
}

/** PATCH /api/v2/settings/time-tracking — managers only, 403 otherwise; partial patch */
export function updateTimeTrackingSettings(
  patch: { hours_per_day?: number; billing_timezone?: string },
): Promise<AxiosResponse<LaravelResource<TimeTrackingSettings>>> {
  return request({
    url: '/api/v2/settings/time-tracking',
    method: 'patch',
    data: patch,
  })
}

/** GET /api/v2/settings/contact-numbering — readable by every user (the create form needs it) */
export function getContactNumberingSettings(): Promise<
  AxiosResponse<LaravelResource<ContactNumberingSettings>>
> {
  return request({
    url: '/api/v2/settings/contact-numbering',
    method: 'get',
  })
}

/** PUT /api/v2/settings/contact-numbering — managers only; 400 with `details` on an invalid pattern */
export function updateContactNumberingSettings(
  payload: ContactNumberingPayload,
): Promise<AxiosResponse<LaravelResource<ContactNumberingSettings>>> {
  return request({
    url: '/api/v2/settings/contact-numbering',
    method: 'put',
    data: payload,
  })
}

export interface EffortLevel {
  /** `^[a-z0-9_]{1,10}$`, unique within the account. */
  key: string
  /** 1–20 characters. */
  label: string
  /** 1–1440, strictly ascending across the levels. */
  minutes: number
}

export interface EffortSettings {
  /** 2–8 levels, ordered by minutes. */
  effort_levels: EffortLevel[]
  /** One of the level keys; assumed for tasks without an own level or estimation. */
  default_effort_level: string
  /** 0–80; share of the capacity kept free for unplanned work. */
  planning_buffer_percent: number
}

/** GET /api/v2/settings/effort — every employee of an account with the tasks app */
export function getEffortSettings(): Promise<AxiosResponse<LaravelResource<EffortSettings>>> {
  return request({
    url: '/api/v2/settings/effort',
    method: 'get',
  })
}

/** PATCH /api/v2/settings/effort — managers only; any subset of the fields, 400 on invalid values */
export function updateEffortSettings(
  partial: Partial<EffortSettings>,
): Promise<AxiosResponse<LaravelResource<EffortSettings>>> {
  return request({
    url: '/api/v2/settings/effort',
    method: 'patch',
    data: partial,
  })
}

export interface ContactDataTypesPayload {
  address: Partial<ContactDataType>[]
  phone: Partial<ContactDataType>[]
  email: Partial<ContactDataType>[]
  website: Partial<ContactDataType>[]
  /** deleted type id => id of the type its entries move to */
  replacements?: Record<string, string>
}

/** GET /api/v2/settings/contact-data-types — every employee */
export function getContactDataTypes(): Promise<AxiosResponse<LaravelResource<ContactDataTypes>>> {
  return request({
    url: '/api/v2/settings/contact-data-types',
    method: 'get',
  })
}

/**
 * PUT /api/v2/settings/contact-data-types — managers only; the four full lists in order (entries
 * without `id` are new). 422 TYPE_IN_USE (with meta.counts) when a used type is dropped without
 * a replacement.
 */
export function updateContactDataTypes(
  data: ContactDataTypesPayload,
): Promise<AxiosResponse<LaravelResource<ContactDataTypes>>> {
  return request({
    url: '/api/v2/settings/contact-data-types',
    method: 'put',
    data,
  })
}
